/* eslint-disable react/no-danger */
import './ViewDocument.scss';
import '../DocumentStatuses.scss';

import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { useDataSet } from 'customHooks/useDataSet';
import { useGuidedNavigationCollection } from 'customHooks/useGuidedNavigationCollection';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { CommentType } from 'models/CommentType';
import { Document } from 'models/Document';
import { DocumentComment } from 'models/DocumentComment';
import { DocumentStatus } from 'models/DocumentStatus';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { SiteSection } from 'models/SiteSection';
import { TargetKeyword } from 'models/TargetKeyword';
import moment from 'moment';
import { Accordion, Button, Col, FloatingLabel, Form, Modal, Row, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { useSingleEntity } from '../../../customHooks/useSingleEntity';
import CommentsContainer from '../CommentsContainer/CommentsContainer';
import { DocumentStatusIdMap } from '../DocumentStatusMap';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ViewDocumentProps { }

const ViewDocument: FC<ViewDocumentProps> = () => {
  const navigate = useNavigate();
  const navigationGuide = useGuidedNavigationCollection();
  const { user } = useAuth0();
  const onTokenExpired = useTokenExpiredHandler();
  const { configRepo, setIsProjectSelectable, notify, selectedProjectId, sendLog } = useContext(AppContext);
  const { updateEntity: updateDocument } = useModifyEntityService<Document>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    sendLog,
    onTokenExpired,
  );
  const { createEntity: createDocumentComment } = useModifyEntityService<DocumentComment>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.DocumentComments,
    sendLog,
    onTokenExpired,
  );

  const { id } = useParams();
  const documentId = parseInt(id!, 10);

  const { entityRequest: documentRequest } = useSingleEntity<Document>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    documentId,
  );
  const document: Document = documentRequest.entity;
  const { entityRequest: statusRequest } = useSingleEntity<DocumentStatus>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.DocumentStatuses,
    document.statusId,
  );
  const status: DocumentStatus = statusRequest.entity;
  const { entityRequest: sectionRequest } = useSingleEntity<SiteSection>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.SiteSections,
    document.siteSectionId,
  );
  const section: SiteSection = sectionRequest.entity;
  const { entityRequest: keywordRequest } = useSingleEntity<TargetKeyword>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.TargetKeywords,
    document.keywordId,
  );
  const keyword: TargetKeyword = keywordRequest.entity;
  const { dataSet /* , setOperators */ } = useDataSet<DocumentComment>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.DocumentComments,
    undefined,
    { DocumentId: documentId },
  );
  const rejections: DocumentComment[] = dataSet.responseData as DocumentComment[];

  const [modalShow, setModalShow] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');

  const changeDocumentStatus = async (statusId: number): Promise<boolean> => {
    let newStatusId = statusId;
    if (typeof statusId !== 'number') {
      newStatusId = parseInt(statusId, 10);
    }
    document.statusId = newStatusId;
    return updateDocument(document);
  };

  const submitRejectionComment = async (comment: string): Promise<boolean | undefined> => {
    // const scheduleService = new ApiService<DocumentComment>(
    //   configRepo!.SCHEDULE_API_BASE_URI,
    //   Entities.DocumentComments,
    // );

    const rejection: DocumentComment = {
      dateRejected: new Date(),
      documentId: document.id,
      userId: user!.sub!,
      userName: user!.name,
      userPicture: user!.picture,
      projectId: selectedProjectId!,
      previousContent: undefined,
      suggestedContent: undefined,
      commentType: CommentType.Comment,
      comments: comment,
    };

    const rejectionResponse = await createDocumentComment(rejection);
    if (rejectionResponse) {
      rejections.push(rejectionResponse);
      return true;
    }
    return false;
  };

  const onApproval = async () => {
    if (await changeDocumentStatus(configRepo!.ACCEPTED_STATUS_ID!)) {
      navigate('/documents');
    } else {
      notify('Unable to approve document.', NotificationType.Error);
      sendLog('unable to approve document', LogLevel.error);
    }
  };

  const onRejection = async () => {
    if (!rejectionComment) {
      notify('Please add a comment along with your rejection.', NotificationType.Warning);
      return;
    }

    if (await changeDocumentStatus(configRepo!.REJECTED_STATUS_ID!)) {
      if (await submitRejectionComment(rejectionComment)) {
        notify('Document has been rejected.', NotificationType.Success);
        setModalShow(false);
      } else {
        notify('Unable to save rejection comment.', NotificationType.Error);
        sendLog('unable to submit rejection comment', LogLevel.error);
      }
    } else {
      notify('Unable to reject document.', NotificationType.Error);
      sendLog('unable to reject document', LogLevel.error);
    }
  };

  const convertNewLines = (message: string | null | undefined): JSX.Element | null => {
    if (message === null || message === undefined || message === '') {
      return null;
    }

    return (
      <div
        style={{
          whiteSpace: "pre-wrap",
          lineHeight: "1.5",
        }}
      >
        {message}
      </div>
    );
  }

  useEffect(() => {
    setIsProjectSelectable(false);

    const startTour = async () => {
      await navigationGuide.startTourFromQueryParams();
    };
    startTour().catch(e => {
      sendLog(`Failed to start tour with error ${e.message}`, LogLevel.error);
    });

  }, []);

  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    if (
      status.id === parseInt(configRepo!.PREPUBLISHED_STATUS_ID, 10) ||
      status.id === parseInt(configRepo!.PUBLISHED_STATUS_ID, 10)
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [status.id]);

  return (
    <div className="ViewDocument">
      <Row>
        <Col md={8} sm={12}>
          <h1>{document.title}</h1>
          {
            (document.imageUrl) ?
              <img src={document.imageUrl} className="header-image img-fluid" alt={document.title} /> :
              <img src="https://placehold.co/300x200?text=No+Image" className="header-image img-fluid" alt={document.title} />
          }

          <div className="short-description">{document.shortDescription}</div>
        </Col>
        <Col md={4} sm={12}>
          <div className="metadata">
            <h2>Publication Information</h2>
            <Table size="sm">
              <tbody>
                <tr>
                  <td className="col-md-6">Date:</td>
                  <td className="col-md-6">
                    {document.plannedDate
                      ? moment(new Date(document.plannedDate)).format('MM/DD/YYYY')
                      : 'UNPLANNED'}
                  </td>
                </tr>
                <tr style={{ display: status ? 'table-row' : 'none' }}>
                  <td className="col-md-6">Status:</td>
                  <td className={`col-md-6 status-type ${DocumentStatusIdMap[document.statusId]}`}>{status ? status.name : 'Not Set'}</td>
                </tr>
                <tr
                  style={{
                    display: document.siteSectionId ? 'table-row' : 'none',
                  }}
                >
                  <td className="col-md-6">Category:</td>
                  <td className="col-md-6">{section.sectionName}</td>
                </tr>
                <tr
                  style={{
                    display: document.articleType ? 'table-row' : 'none',
                  }}
                >
                  <td className="col-md-6">Type:</td>
                  <td className="col-md-6">{document.articleType?.replace('_', ' ')}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="metadata">
            <h2>Target Keyword</h2>
            <Table size="sm">
              <tbody>
                <tr>
                  <td className="col-md-6">Keyword:</td>
                  <td className="col-md-6">{keyword.seoKeyword}</td>
                </tr>
                <tr style={{ display: status ? 'table-row' : 'none' }}>
                  <td className="col-md-6">Search Volume:</td>
                  <td className="col-md-6">{keyword.seoMonthlySearchVolume ?? 'N/A'}</td>
                </tr>
                <tr>
                  <td className="col-md-6">Difficulty:</td>
                  <td className="col-md-6">{keyword.seoDifficulty ?? 'N/A'}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      <Row>
        {rejections && rejections.length > 0 ? (
          <>
            <Col md={9} sm={12}>
              <div dangerouslySetInnerHTML={{ __html: document.content }} />
            </Col>
            <CommentsContainer rejectionComments={rejections} />
          </>
        ) : (
          <Col md={12} sm={12}>
            <div dangerouslySetInnerHTML={{ __html: document.content }} />
          </Col>
        )}
      </Row>

      <Row>
        <Col md={12}>
          <h3>Social Media Posts</h3>
        </Col>
        <Accordion as={Col} md={12} defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <i className="bi bi-facebook" /> Facebook
            </Accordion.Header>
            <Accordion.Body>{convertNewLines(document.fbMessageTmp)}</Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <i className="bi bi-twitter" /> Twitter
            </Accordion.Header>
            <Accordion.Body>{convertNewLines(document.twMessageTmp)}</Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <i className="bi bi-linkedin" /> LinkedIn
            </Accordion.Header>
            <Accordion.Body>{convertNewLines(document.liMessageTmp)}</Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Row>

      <Modal show={modalShow} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Header>
          <Modal.Title id="contained-modal-title-vcenter">Reject Article</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <FloatingLabel controlId="tbxComments" label="Reason for rejection">
              <Form.Control
                as="textarea"
                placeholder="Leave your comments here"
                style={{ height: '100px' }}
                required
                value={rejectionComment}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setRejectionComment(e.target.value)
                }
              />
            </FloatingLabel>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" variant="danger" onClick={onRejection}>
            Reject Article
          </Button>
          <Button variant="light" onClick={() => setModalShow(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Row className="fixed-bottom approval-bar">
        <Col md={12} className="py-2">
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div className="ml-2 text-muted small xs-display-none">
              <i className={`bi ${document.plannedDate ? "bi-calendar-check" : "bi-calendar-x"} me-2`} />
              {document.plannedDate ? `Scheduled on ${moment(document.plannedDate).format("MMMM D, YYYY")} at ${moment(document.plannedDate).format("h:mm A")}` : "Not scheduled yet"}
            </div>
            <div className='d-flex align-items-center'>
              <Button variant="success" className="btn-approve" onClick={onApproval} disabled={disabled}>
                <i className="bi bi-check xs-hide" /> Approve
              </Button>
              <Button
                variant="danger"
                className="btn-reject"
                onClick={() => setModalShow(true)}
                disabled={disabled}
              >
                <i className="bi bi-x xs-hide" /> Reject
              </Button>
              <a className="btn-reject btn btn-light" href={`/documents/edit/${documentId}`}>
                <i className="bi bi-pencil-fill xs-hide" /> Edit
              </a>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ViewDocument;
